/**
 * Access-denied page.
 *
 * Explains in plain words why the page did not open, names the organization
 * the user works in now, and offers the existing organization switcher. After
 * a switch the user lands on the page they asked for (`next`). After a
 * platform refusal it also names the organization that grants platform
 * administration on a one-click switch button (agion/Agion#2310). The page
 * never switches organization by itself: a link must not change what the user
 * is signed in to.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { Card, Button, Alert, OrgSwitcher } from '@agion/ui'
import { useAuth, useOrganization } from '@agion/auth'
import { ShieldX } from 'lucide-react'
import { platformOrganizationToOffer, readAccessDeniedParams } from '@/core/infra_access_denied'
import { useOrgSwitcherLabels, useOrganizationErrorMessage } from '@/components/infra_org_switcher_labels'

export function AccessDeniedPage() {
  const { t } = useTranslation(['pages'])
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { active, all, isLoading, errorCode, switchOrg } = useOrganization()
  const switcherLabels = useOrgSwitcherLabels()
  // agion/Agion#2321: the same mapping InfraOrgSwitcher uses — no raw
  // @agion/auth error text reaches this page either.
  const organizationErrorMessage = useOrganizationErrorMessage(errorCode)
  const headingRef = useRef<HTMLHeadingElement>(null)

  const { next, reason } = readAccessDeniedParams(location.search)
  const platform = reason === 'platform-admin'
  const organizationName = active?.name ?? user?.organizationName
  const canSwitch = isLoading || all.length > 1
  const platformOrganization = platform ? platformOrganizationToOffer(all, user?.organizationId) : null
  const [switching, setSwitching] = useState(false)

  const switchToPlatformOrganization = async (organizationId: string) => {
    setSwitching(true)
    try {
      await switchOrg(organizationId, { redirectTo: next })
    } finally {
      setSwitching(false)
    }
  }

  // Move focus to the explanation so screen readers announce why the page changed.
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <div
      data-testid="access-denied-page"
      className="min-h-full flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4"
    >
      <Card className="max-w-lg w-full">
        <div className="p-8 space-y-4">
          <ShieldX size={48} aria-hidden="true" className="text-red-500 mx-auto" />
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-xl font-bold text-center text-slate-900 dark:text-slate-100 focus:outline-none focus:shadow-none"
          >
            {platform
              ? t('pages:accessDenied.platformTitle', 'This page is for the Agion platform team')
              : t('pages:accessDenied.generalTitle', 'You cannot open this page')}
          </h1>

          <div className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
            {organizationName && (
              <p>
                {t('pages:accessDenied.currentOrganization', 'You are working in {{organization}}.', {
                  organization: organizationName,
                })}
              </p>
            )}
            <p>
              {platform
                ? t(
                    'pages:accessDenied.platformExplanation',
                    'Platform pages, such as alerts, edge hosts and rollout controls, open only while the Agion platform organization is selected. What you can open depends on the organization you work in.'
                  )
                : t(
                    'pages:accessDenied.generalExplanation',
                    'Your role in this organization does not give access to this page.'
                  )}
            </p>
            {next !== '/' && (
              <p>
                {t('pages:accessDenied.requestedPage', 'Page you asked for:')}{' '}
                <code className="break-all">{next}</code>
              </p>
            )}
          </div>

          {platformOrganization && (
            <Button
              variant="primary"
              fullWidth
              isLoading={switching}
              onClick={() => switchToPlatformOrganization(platformOrganization.id)}
            >
              {t('pages:accessDenied.switchToPlatformOrganization', 'Switch to {{organization}}', {
                organization: platformOrganization.name,
              })}
            </Button>
          )}

          {canSwitch ? (
            <div className="space-y-2">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {platform
                  ? t(
                      'pages:accessDenied.platformSwitchHint',
                      'If you are on the Agion platform team, choose the Agion platform organization below. You go straight back to the page you asked for.'
                    )
                  : t(
                      'pages:accessDenied.generalSwitchHint',
                      'If you have access through another organization, choose it below. You go straight back to the page you asked for.'
                    )}
              </p>
              <OrgSwitcher
                organizations={all}
                currentOrgId={active?.id}
                onSwitch={organizationId => switchOrg(organizationId, { redirectTo: next })}
                variant="header"
                isLoading={isLoading}
                labels={{
                  ...switcherLabels,
                  tooltip: t('pages:accessDenied.chooseOrganization', 'Choose the organization to work in'),
                }}
                className="w-full justify-between border border-slate-200 dark:border-slate-700"
              />
            </div>
          ) : (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {platform
                ? t(
                    'pages:accessDenied.platformAsk',
                    'Only members of the Agion platform team can open these pages. If you need this information, ask an Agion platform administrator.'
                  )
                : t('pages:accessDenied.generalAsk', 'To get access, ask an administrator of your organization.')}
            </p>
          )}

          {organizationErrorMessage && (
            <Alert variant="warning">
              {organizationErrorMessage}
            </Alert>
          )}

          <div className="flex flex-wrap justify-center gap-2 pt-2">
            <Button variant="outline" onClick={() => window.history.back()}>
              {t('pages:accessDenied.goBack', 'Go back')}
            </Button>
            <Button variant="ghost" onClick={() => navigate('/')}>
              {t('pages:accessDenied.goToStart', 'Go to the start page')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
